import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Folder, ExternalLink, Play } from "lucide-react";
import { zupeeCampaignVideos, youtubeThumbAt, YT_THUMB_TIERS, youtubeEmbed } from "../data/videos";

const FOLDER_ID = "1CNtwjGJSZkIEiLCm6aVOXYaD2iQBrNPn";
const FOLDER_URL = `https://drive.google.com/drive/folders/${FOLDER_ID}?usp=sharing`;

const CAMPAIGN = {
  id: "cc-01",
  title: "Zupee",
  subtitle: "Starring Campaign",
  note: "Salman Khan · Amitabh Bachchan · Harbhajan Singh · Kapil Sharma — brand films, concept-to-cut shorts and campaign deliverables.",
};

// Multi-tier thumbnail img — prefers the pre-baked static poster, falls back
// to YouTube's live CDN if the static file is missing.
function YtThumb({ youtubeId, className }) {
  const [stage, setStage] = useState(0); // 0 = static, 1..5 = live tiers
  const src =
    stage === 0
      ? `/yt-thumbs/${youtubeId}.jpg`
      : youtubeThumbAt(youtubeId, YT_THUMB_TIERS[stage - 1]);
  return (
    <img
      key={youtubeId + stage}
      src={src}
      alt=""
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => stage < YT_THUMB_TIERS.length && setStage((s) => s + 1)}
      className={className}
    />
  );
}

function VideoPlayer({ youtubeId, onClose }) {
  if (!youtubeId) return null;
  return (
    <Dialog open={!!youtubeId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="campaign-video-modal"
        className="z-[95] max-w-5xl border-white/15 bg-[#060606] p-0"
      >
        <DialogTitle className="sr-only">Zupee Campaign Video</DialogTitle>
        <DialogDescription className="sr-only">Zupee × Salman Khan campaign deliverable</DialogDescription>
        <div className="aspect-video w-full overflow-hidden bg-black">
          <iframe
            title="Zupee campaign"
            src={youtubeEmbed(youtubeId)}
            className="h-full w-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AutoSlideGallery({ onPick }) {
  // Index that advances every 3.2 s. Pauses on hover (user can read a frame).
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % zupeeCampaignVideos.length);
    }, 3200);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Hero stage — the active thumbnail */}
      <div className="relative aspect-video overflow-hidden rounded-xl border border-white/10 bg-black">
        {zupeeCampaignVideos.map((v, i) => (
          <button
            key={v.youtubeId}
            data-testid={`campaign-slide-${v.youtubeId}`}
            onClick={() => onPick(v.youtubeId)}
            className={`absolute inset-0 transition-opacity duration-700 ease-out ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
            aria-hidden={i !== index}
            tabIndex={i === index ? 0 : -1}
          >
            <YtThumb
              youtubeId={v.youtubeId}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/5 to-black/15 transition-colors duration-500 hover:from-black/55" />
            {/* Camera reticles */}
            <span className="absolute left-3 top-3 h-4 w-4 border-l border-t border-[#CBFF3A]/70" />
            <span className="absolute right-3 top-3 h-4 w-4 border-r border-t border-[#CBFF3A]/70" />
            <span className="absolute bottom-3 left-3 h-4 w-4 border-b border-l border-[#CBFF3A]/70" />
            <span className="absolute bottom-3 right-3 h-4 w-4 border-b border-r border-[#CBFF3A]/70" />
            {/* Index chip */}
            <span className="absolute left-4 top-4 flex items-center gap-2 rounded-full border border-[#CBFF3A]/40 bg-black/55 px-3 py-1 font-hud text-[9px] uppercase tracking-[0.2em] text-[#CBFF3A] backdrop-blur-sm">
              <Folder className="h-3 w-3" /> Collection ·{" "}
              {String(i + 1).padStart(2, "0")}/
              {String(zupeeCampaignVideos.length).padStart(2, "0")}
            </span>
            {/* Center play */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-500 hover:opacity-100">
              <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[#CBFF3A] bg-black/55 backdrop-blur-sm">
                <Play className="h-5 w-5 translate-x-[1px] fill-[#CBFF3A] text-[#CBFF3A]" />
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Thumbnail strip — click a specific tile to jump to it (and play) */}
      <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-5 md:grid-cols-9">
        {zupeeCampaignVideos.map((v, i) => (
          <button
            key={v.youtubeId + "-strip"}
            data-testid={`campaign-strip-${v.youtubeId}`}
            onClick={() => {
              setIndex(i);
              onPick(v.youtubeId);
            }}
            className={`group relative aspect-video overflow-hidden rounded border transition-all duration-300 ${
              i === index
                ? "border-[#CBFF3A] shadow-[0_0_14px_rgba(203,255,58,0.35)]"
                : "border-white/10 opacity-70 hover:opacity-100"
            }`}
          >
            <YtThumb
              youtubeId={v.youtubeId}
              className="h-full w-full object-cover"
            />
            {i === index && (
              <span className="absolute inset-0 ring-1 ring-inset ring-[#CBFF3A]" />
            )}
          </button>
        ))}
      </div>

      {/* Progress ticker — count of current slide */}
      <div className="pointer-events-none mt-4 flex items-center gap-3">
        <span className="font-hud text-[10px] uppercase tracking-[0.3em] text-white/40">
          Auto-cycle
        </span>
        <div className="relative h-[2px] flex-1 overflow-hidden rounded bg-white/10">
          <div
            key={index + (paused ? "p" : "")}
            className="absolute inset-y-0 left-0 bg-[#CBFF3A]"
            style={{
              animation: paused ? "none" : "campaignProgress 3.2s linear forwards",
            }}
          />
        </div>
        <span className="font-hud text-[10px] uppercase tracking-[0.3em] text-[#CBFF3A]/80">
          {String(index + 1).padStart(2, "0")} /{" "}
          {String(zupeeCampaignVideos.length).padStart(2, "0")}
        </span>
      </div>
    </div>
  );
}

export default function CreativeCampaigns() {
  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <section
      id="creative-campaigns"
      data-testid="creative-campaigns-section"
      className="relative z-10 mx-auto max-w-[1400px] px-6 pt-20 md:px-16 md:pt-28"
    >
      <style>{`
        @keyframes campaignProgress {
          from { transform: translateX(-100%); }
          to   { transform: translateX(0%); }
        }
      `}</style>

      <div className="mb-10 flex flex-col gap-3">
        <span className="font-hud text-[11px] uppercase tracking-[0.3em] text-[#CBFF3A]/80">
          / Creative Campaigns
        </span>
        <h2 className="font-display text-4xl font-600 tracking-tight text-[#ECEAE3] md:text-5xl">
          The <em className="not-italic text-[#CBFF3A]">Campaign</em> Archive
        </h2>
        <p className="max-w-xl font-body text-white/55">
          Click any thumbnail to play a Zupee campaign deliverable — the gallery
          auto-cycles every few seconds and pauses when you hover.
        </p>
      </div>

      <motion.div
        data-testid="creative-campaign-card-cc-01"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="rounded-2xl border border-white/10 bg-[#0d0d0f] p-5 md:p-7"
      >
        <AutoSlideGallery onPick={setActiveVideo} />

        <div className="mt-7 flex flex-col items-start justify-between gap-5 border-t border-white/10 pt-6 md:flex-row md:items-center">
          <div className="min-w-0">
            <p className="font-hud text-[10px] uppercase tracking-[0.3em] text-[#CBFF3A]/80">
              {CAMPAIGN.subtitle}
            </p>
            <h3 className="mt-2 font-display text-2xl font-600 tracking-tight text-white md:text-3xl">
              {CAMPAIGN.title}
            </h3>
            <p className="mt-2 max-w-2xl font-body text-sm text-white/55">
              {CAMPAIGN.note}
            </p>
          </div>
          <a
            href={FOLDER_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="campaign-open-drive"
            className="flex shrink-0 items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-hud text-[10px] uppercase tracking-widest text-white/70 transition-colors hover:border-[#CBFF3A] hover:text-[#CBFF3A]"
          >
            Open full folder <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </motion.div>

      <VideoPlayer youtubeId={activeVideo} onClose={() => setActiveVideo(null)} />
    </section>
  );
}
